import { Box, Card, Heading, Item, RadioInput, SearchSelect, TextBlock } from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "./grading-workflow-selection.scss";
import { WorkflowForGrading } from "services/asset-management/AssetGradingService";

import testIds from "testIds.json";

interface Props {
    setTemplateSelected: (selected: boolean) => void;
    setWorkflowUuid: (workflowUuid?: string) => void;
    workflows: WorkflowForGrading[];
    templates: WorkflowForGrading[];
    workflowUuid?: string;
    isRerunning?: boolean;
    hasMissingWorkflowError?: boolean;
}

export const toSearchSelectItems = (workflows: WorkflowForGrading[]): Item[] =>
    workflows.map((workflow) => ({ value: workflow.uuid, label: workflow.name }));

export function isTemplateWorkflow(workflowUuid: string | undefined, templates: WorkflowForGrading[]): boolean {
    return workflowUuid !== undefined && templates.some((template) => template.uuid === workflowUuid);
}

export const GradingWorkflowSelection = (props: Props) => {
    const { t } = useTranslation();
    const [templateSelected, setTemplateSelected] = React.useState<boolean>(
        isTemplateWorkflow(props.workflowUuid, props.templates)
    );

    React.useEffect(() => {
        props.setTemplateSelected(templateSelected);
    }, []);

    const selectTemplate = (selected: boolean) => {
        if (selected !== templateSelected) {
            props.setWorkflowUuid(undefined);
        }
        setTemplateSelected(selected);
        props.setTemplateSelected(selected);
    };

    const selectWorkflow = (value: string | number) => props.setWorkflowUuid(String(value));

    const selectedWorkflowName = [...props.workflows, ...props.templates].find(
        (workflow) => workflow.uuid === props.workflowUuid
    )?.name;

    return (
        <>
            <Heading variant={"SUBTITLE_1"} disableBottomSpacing>
                {t("AssetManagement.value.smartGrading.stepOne.selectGradingWorkflow")}
            </Heading>
            {props.isRerunning && (
                <TextBlock disableBottomSpacing>
                    {t("AssetManagement.value.smartGrading.stepOne.currentWorkflow", {
                        workflow: selectedWorkflowName ?? props.workflowUuid,
                    })}
                </TextBlock>
            )}
            <Card className={style.borderColor}>
                <Box justifyContent="SpaceBetween" columnLayout gap={"Xs"}>
                    <RadioInput
                        testIds={{ input: testIds.workArea.asset.customView.value.customWorkflowRadioButton }}
                        onClick={() => selectTemplate(false)}
                        checked={!templateSelected}
                        id={"customWorkflow"}
                        name={"workflowGroup"}
                        label={t("AssetManagement.value.smartGrading.stepOne.customGradingWorkflow")}
                    />
                    <SearchSelect
                        key={props.workflowUuid ?? ""}
                        testIds={{ input: testIds.workArea.asset.customView.value.customWorkflowSearchSelect }}
                        selectedByDefault={props.workflowUuid}
                        onSelection={selectWorkflow}
                        items={toSearchSelectItems(props.workflows)}
                        name={t("AssetManagement.value.smartGrading.stepOne.selectGradingWorkflow")}
                        placeholder={t("AssetManagement.value.smartGrading.stepOne.selectWorkflow")}
                        closeOnSelection
                        error={
                            !templateSelected && props.hasMissingWorkflowError && !props.workflowUuid
                                ? t("Common.selectValue")
                                : undefined
                        }
                    />
                </Box>
            </Card>
            <Card className={style.borderColor}>
                <Box justifyContent="SpaceBetween" columnLayout gap={"Xs"}>
                    <RadioInput
                        testIds={{ input: testIds.workArea.asset.customView.value.blanccoWorkflowRadioButton }}
                        onClick={() => selectTemplate(true)}
                        checked={templateSelected}
                        id={"blanccoWorkflow"}
                        name={"workflowGroup"}
                        label={t("AssetManagement.value.smartGrading.stepOne.standardBlanccoGradingWorkflow")}
                    />
                    <SearchSelect
                        key={props.workflowUuid ?? ""}
                        testIds={{ input: testIds.workArea.asset.customView.value.blanccoWorkflowSearchSelect }}
                        selectedByDefault={props.workflowUuid}
                        items={toSearchSelectItems(props.templates)}
                        onSelection={selectWorkflow}
                        name={t("AssetManagement.value.smartGrading.stepOne.selectGradingWorkflow")}
                        placeholder={t("AssetManagement.value.smartGrading.stepOne.selectWorkflow")}
                        closeOnSelection
                        error={
                            templateSelected && props.hasMissingWorkflowError && !props.workflowUuid
                                ? t("Common.selectValue")
                                : undefined
                        }
                    />
                </Box>
            </Card>
        </>
    );
};
